import { useEffect, useState } from "react";
import { api, fmtError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export function bookletRows(exams) {
  const seen = new Set();
  return exams.filter(exam => {
    const key = exam.booklet_group?.id || exam.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function CorrespondenceTable({ group }) {
  if (!group?.members?.length) return null;
  const positions = [...new Set(group.members.flatMap(m => Object.values(m.mapping || {}).map(x => x.position)))].sort((a, b) => a - b);
  if (!positions.length) return null;
  return <div className="max-h-80 overflow-auto border border-border mt-4">
    <table className="w-full text-sm text-left" aria-label="Correspondência das questões entre cadernos">
      <caption className="p-3 text-left">Cada linha representa a mesma questão nos diferentes cadernos.</caption>
      <thead className="sticky top-0 bg-card"><tr>{group.members.map(m => <th className="p-3 border-b" key={m.exam_id}>Caderno {m.number}</th>)}</tr></thead>
      <tbody>{positions.map(p => <tr key={p}>{group.members.map(m => {
        const local = Object.entries(m.mapping || {}).find(([, link]) => link.position === p)?.[0];
        return <td className="p-2 border-b" key={m.exam_id}>{local ? `Questão ${local}` : "—"}</td>;
      })}</tr>)}</tbody>
    </table>
  </div>;
}

export function BookletNavigation({ group, examId, onSelect, onManage }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [showTable, setShowTable] = useState(false);
  useEffect(() => {
    let active = true;
    setDetail(null); setError("");
    api.get(`/exam-booklet-groups/${group.id}`).then(r => { if (active) setDetail(r.data); })
      .catch(e => { if (active) setError(fmtError(e)); });
    return () => { active = false; };
  }, [group.id, group.revision, examId]);
  return <section className="max-w-[1400px] mx-auto px-8 pt-6" aria-label="Cadernos da prova">
    <h1 className="font-display text-3xl font-bold">{group.name}</h1>
    <div className="flex flex-wrap items-center gap-4 mt-4">
      <label>Caderno <select aria-label="Escolher caderno" className="border p-2 bg-card ml-2" value={examId} onChange={e => onSelect(e.target.value)}>
        {group.members.map(m => <option key={m.exam_id} value={m.exam_id}>Tipo {m.number}</option>)}
      </select></label>
      <Button variant="outline" onClick={() => setShowTable(v => !v)}>Tabela de correspondência</Button>
      {detail?.can_write && <Button variant="outline" onClick={() => onManage(group)}>Organizar cadernos</Button>}
    </div>
    <p className="text-sm text-muted-foreground mt-3">O gabarito é único: alterações e anulações são aplicadas às questões correspondentes em todos os cadernos. Cartões e resultados abaixo pertencem ao tipo selecionado.</p>
    {error && <p role="alert">{error}</p>}
    {detail?.mapping_stale && <p role="alert" className="text-destructive mt-3">Um caderno mudou. Confira e salve novamente a correspondência antes de alterar o gabarito.</p>}
    {showTable && detail && <CorrespondenceTable group={detail} />}
  </section>;
}

export default function ExamBookletsDialog({ open, onClose, exams, group, onSaved }) {
  const [name, setName] = useState("");
  const [members, setMembers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setName(group?.name || ""); setMembers(group?.members || []); setPreview(null); setError("");
  }, [open, group]);
  const eligible = exams.filter(e => e.can_write && (!e.booklet_group || e.booklet_group.id === group?.id));
  const change = fn => { fn(); setPreview(null); setError(""); };
  const payload = { name, members: members.map(m => ({ exam_id: m.exam_id, number: Number(m.number) })), revision: group?.revision || 0 };
  const verify = async () => {
    setBusy(true); setError("");
    try { setPreview((await api.post("/exam-booklet-groups/preview", payload)).data); }
    catch (e) { setError(fmtError(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true); setError("");
    try {
      if (group) await api.put(`/exam-booklet-groups/${group.id}`, payload);
      else await api.post("/exam-booklet-groups", payload);
      toast.success("Prova organizada em cadernos"); await onSaved(); onClose();
    } catch (e) { setError(fmtError(e)); } finally { setBusy(false); }
  };
  const detach = async () => {
    if (!window.confirm("Separar os cadernos novamente? As provas, gabaritos e respostas serão preservados; alterações futuras deixarão de ser compartilhadas.")) return;
    setBusy(true); setError("");
    try {
      await api.delete(`/exam-booklet-groups/${group.id}`);
      toast.success("Cadernos separados, com todos os registros preservados"); await onSaved(); onClose();
    } catch (e) { setError(fmtError(e)); } finally { setBusy(false); }
  };
  return <Dialog open={open} onOpenChange={value => { if (!value && !busy) onClose(); }}><DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
    <DialogHeader><DialogTitle>Uma prova, vários cadernos</DialogTitle></DialogHeader>
    <p className="text-sm">Selecione as versões da mesma prova. A correspondência confere enunciados, imagens, alternativas, gabaritos e anulações antes de reunir os cadernos.</p>
    <label>Nome da prova<input disabled={busy} aria-label="Nome da prova principal" className="border bg-card p-2 w-full" value={name} maxLength={140} onChange={e => change(() => setName(e.target.value))} /></label>
    <div className="max-h-64 overflow-auto border p-3">{eligible.map(exam => {
      const member = members.find(m => m.exam_id === exam.id);
      return <div className="flex items-center gap-3 py-2" key={exam.id}>
        <label className="flex-1"><input type="checkbox" checked={Boolean(member)} disabled={busy} onChange={e => change(() => setMembers(current => e.target.checked ? [...current, { exam_id: exam.id, number: Math.max(0, ...current.map(m => Number(m.number))) + 1 }] : current.filter(m => m.exam_id !== exam.id)))} /> {exam.name}</label>
        {member && <label>Tipo <input disabled={busy} aria-label={`Tipo de caderno de ${exam.name}`} type="number" min={1} max={9999} className="border bg-card p-2 w-20" value={member.number} onChange={e => change(() => setMembers(current => current.map(m => m.exam_id === exam.id ? { ...m, number: e.target.value } : m)))} /></label>}
      </div>;
    })}</div>
    {error && <p role="alert" className="text-destructive">{error}</p>}
    {preview && <><p>{preview.members.length} cadernos conferidos. As respostas dos alunos e os arquivos existentes serão preservados.</p><CorrespondenceTable group={preview} /></>}
    <div className="flex flex-wrap justify-end gap-3">{group && <Button variant="outline" disabled={busy} onClick={detach}>Separar cadernos</Button>}<Button variant="outline" disabled={busy} onClick={onClose}>Cancelar</Button><Button disabled={busy || members.length < 2 || !name.trim()} onClick={preview ? save : verify}>{busy ? "Conferindo…" : preview ? "Salvar prova com cadernos" : "Conferir correspondência"}</Button></div>
  </DialogContent></Dialog>;
}
